// @ts-nocheck
import React, { useState, useEffect } from "react";
import {
  Camera,
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  Menu,
  Search,
  BarChart3,
  LayoutDashboard,
  AlertOctagon,
  FileText,
  Settings,
  User,
  MapPin,
  Check,
  X,
  ClipboardList,
  Filter,
  Mic,
  QrCode,
  Sparkles,
  PieChart,
} from "lucide-react";

// --- MOCK DATA ---
const dummySites = [
  {
    id: 1,
    name: "Auckland City HQ",
    region: "North Island",
    branch: "Auckland CBD",
    client: "Acme Corp",
    project: "Annual Safety Audit",
  },
  {
    id: 2,
    name: "Wellington Central",
    region: "North Island",
    branch: "Wellington",
    client: "TechNova",
    project: "Q3 Facility Maintenance",
  },
];

const auditTemplate = {
  healthAndSafety: [
    {
      id: "hs1",
      name: "Fire Extinguisher accessible & in date",
      type: "passfail",
    },
    { id: "hs2", name: "Hazard Register visible", type: "passfail" },
    { id: "hs3", name: "Exit Signs illuminated", type: "1-3" },
  ],
  reception: [
    { id: "rec1", name: "Desk cleanliness", type: "1-5" },
    { id: "rec2", name: "Windows and doors clear", type: "1-5" },
    { id: "rec3", name: "Carpets vacuumed", type: "passfail" },
  ],
};

const mockCorrectiveActions = [
  {
    id: "CA-1042",
    site: "Auckland City HQ",
    element: "Fire Extinguisher",
    priority: "High",
    status: "Open",
    date: "2026-06-25",
    auditor: "Jane Doe",
  },
  {
    id: "CA-1041",
    site: "Wellington Central",
    element: "Kitchen Sink",
    priority: "Medium",
    status: "Assigned",
    date: "2026-06-24",
    auditor: "John Smith",
  },
];

export default function App() {
  const [platformView, setPlatformView] = useState("auditor"); // 'auditor' or 'manager'

  return (
    <div className="min-h-screen bg-slate-100 font-sans text-slate-800">
      {/* Top Demo Navigation Bar */}
      <div className="bg-slate-900 text-white p-3 flex justify-between items-center shadow-lg relative z-50">
        <div className="flex items-center space-x-2">
          <div className="font-bold text-xl tracking-tight text-blue-400">
            CMS
            <span className="text-white text-sm font-normal ml-2">
              Prototype
            </span>
          </div>
        </div>
        <div className="flex bg-slate-800 rounded-lg p-1">
          <button
            onClick={() => setPlatformView("auditor")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              platformView === "auditor"
                ? "bg-blue-600 shadow-sm"
                : "hover:bg-slate-700"
            }`}
          >
            📱 Auditor App
          </button>
          <button
            onClick={() => setPlatformView("manager")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              platformView === "manager"
                ? "bg-blue-600 shadow-sm"
                : "hover:bg-slate-700"
            }`}
          >
            💻 Management Portal
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex justify-center items-start pt-8 pb-12 px-4 h-[calc(100vh-64px)] overflow-hidden">
        {platformView === "auditor" ? <AuditorMobileApp /> : <ManagerPortal />}
      </div>
    </div>
  );
}

/* ==========================================================================
   AUDITOR MOBILE APP PROTOTYPE
   ========================================================================== */
function AuditorMobileApp() {
  const [step, setStep] = useState("select-site"); // select-site, activities, auditing, review
  const [selectedSite, setSelectedSite] = useState(null);
  const [currentActivity, setCurrentActivity] = useState(null);
  const [scores, setScores] = useState({});
  const [criticalFails, setCriticalFails] = useState({});
  const [hsComplete, setHsComplete] = useState(false);
  const [isDictating, setIsDictating] = useState(false);

  // Check if H&S is complete
  useEffect(() => {
    if (scores && auditTemplate.healthAndSafety) {
      const allHsScored = auditTemplate.healthAndSafety.every(
        (item) => scores[item.id] !== undefined
      );
      const allCriticalsHandled = auditTemplate.healthAndSafety.every(
        (item) => {
          if (!criticalFails[item.id]) return true; // Not a critical fail
          return (
            criticalFails[item.id].comment &&
            criticalFails[item.id].photos?.length > 0
          ); // Min 1 photo required
        }
      );
      setHsComplete(allHsScored && allCriticalsHandled);
    }
  }, [scores, criticalFails]);

  const handleScore = (elementId, scoreValue, type) => {
    const isCritical =
      (type === "passfail" && scoreValue === "Fail") ||
      (type === "1-3" && scoreValue === 1) ||
      (type === "1-5" && scoreValue === 1);

    setScores((prev) => ({ ...prev, [elementId]: scoreValue }));

    if (isCritical) {
      setCriticalFails((prev) => ({
        ...prev,
        [elementId]: { comment: "", photos: [] },
      }));
    } else {
      const newFails = { ...criticalFails };
      delete newFails[elementId];
      setCriticalFails(newFails);
    }
  };

  const updateCriticalFail = (elementId, field, value) => {
    setCriticalFails((prev) => ({
      ...prev,
      [elementId]: { ...prev[elementId], [field]: value },
    }));
  };

  const addPhoto = (elementId) => {
    setCriticalFails((prev) => {
      const currentPhotos = prev[elementId].photos || [];
      if (currentPhotos.length >= 3) return prev; // Max 3 photos
      return {
        ...prev,
        [elementId]: {
          ...prev[elementId],
          photos: [...currentPhotos, `photo_${currentPhotos.length + 1}.jpg`],
        },
      };
    });
  };

  const calculateScore = () => {
    let totalMax = 0;
    let earned = 0;

    Object.entries(scores).forEach(([id, val]) => {
      const element = [
        ...auditTemplate.healthAndSafety,
        ...auditTemplate.reception,
      ].find((e) => e.id === id);
      if (!element) return;

      if (element.type === "passfail") {
        totalMax += 1;
        if (val === "Pass") earned += 1;
      } else if (element.type === "1-3") {
        totalMax += 3;
        earned += val;
      } else if (element.type === "1-5") {
        totalMax += 5;
        earned += val;
      }
    });

    return totalMax === 0 ? 0 : Math.round((earned / totalMax) * 100);
  };

  return (
    <div className="w-[400px] h-[800px] bg-white rounded-[2rem] shadow-2xl overflow-hidden border-[8px] border-slate-800 relative flex flex-col max-h-full">
      {/* Phone Status Bar */}
      <div className="h-6 bg-slate-900 w-full flex justify-between items-center px-4 text-[10px] text-white">
        <span>09:41</span>
        <div className="flex space-x-1">
          <span>LTE</span>
          <span>100%</span>
        </div>
      </div>

      {/* App Header */}
      <div className="bg-blue-700 text-white p-4 shadow-md flex items-center shrink-0">
        {step !== "select-site" && (
          <button
            onClick={() =>
              setStep(step === "auditing" ? "activities" : "select-site")
            }
            className="mr-3"
          >
            <ChevronRight className="rotate-180 w-6 h-6" />
          </button>
        )}
        <h1 className="font-semibold text-lg flex-1">
          {step === "select-site"
            ? "My Audits"
            : step === "activities"
            ? selectedSite?.name
            : currentActivity === "hs"
            ? "Health & Safety"
            : "Reception"}
        </h1>
        <Menu className="w-6 h-6" />
      </div>

      {/* App Body */}
      <div className="flex-1 overflow-y-auto bg-slate-50">
        {/* STEP 1: Select Site */}
        {step === "select-site" && (
          <div className="p-4 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search sites..."
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
              />
            </div>

            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mt-6 mb-2">
              Today's Route
            </h2>

            <button className="w-full mb-4 bg-white border-2 border-dashed border-slate-300 text-slate-600 font-semibold py-3 rounded-xl flex items-center justify-center hover:bg-slate-50 transition-colors">
              <QrCode className="w-5 h-5 mr-2" />
              Scan Site QR Code
            </button>

            {dummySites.map((site) => (
              <div
                key={site.id}
                onClick={() => {
                  setSelectedSite(site);
                  setStep("activities");
                }}
                className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex items-center active:scale-95 transition-transform cursor-pointer"
              >
                <div className="bg-blue-100 p-3 rounded-full mr-4 text-blue-600">
                  <MapPin className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-800 text-lg">
                    {site.name}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {site.client} • {site.project}
                  </p>
                </div>
                <ChevronRight className="text-slate-400" />
              </div>
            ))}
          </div>
        )}

        {/* STEP 2: Activities Gateway */}
        {step === "activities" && (
          <div className="p-4 space-y-4">
            <div className="bg-white p-5 rounded-xl shadow-sm mb-6 border-l-4 border-blue-500">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-bold text-slate-800 text-lg">
                    {selectedSite?.client}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {selectedSite?.project}
                  </p>
                </div>
                <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded">
                  Target: 90%
                </span>
              </div>

              <div className="mt-4">
                <div className="flex justify-between text-xs font-semibold text-slate-500 mb-1">
                  <span>Overall Progress</span>
                  <span>{Object.keys(scores).length} of 6 Elements</span>
                </div>
                <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-blue-500 h-full transition-all"
                    style={{
                      width: `${(Object.keys(scores).length / 6) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>

            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Mandatory Gateway
            </h2>
            <div
              onClick={() => {
                setCurrentActivity("hs");
                setStep("auditing");
              }}
              className={`p-4 rounded-xl flex items-center justify-between cursor-pointer border ${
                hsComplete
                  ? "bg-green-50 border-green-200"
                  : "bg-red-50 border-red-200 shadow-sm"
              }`}
            >
              <div className="flex items-center">
                <div
                  className={`p-2 rounded-lg mr-3 ${
                    hsComplete
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-600"
                  }`}
                >
                  <AlertOctagon className="w-6 h-6" />
                </div>
                <div>
                  <h3
                    className={`font-bold text-lg ${
                      hsComplete ? "text-green-800" : "text-red-900"
                    }`}
                  >
                    Health & Safety
                  </h3>
                  <p
                    className={`text-sm ${
                      hsComplete ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {hsComplete ? "Completed" : "Action Required First"}
                  </p>
                </div>
              </div>
              {hsComplete && <CheckCircle className="text-green-500 w-6 h-6" />}
            </div>

            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mt-8 mb-2">
              Standard Areas
            </h2>

            <div
              onClick={() => {
                if (hsComplete) {
                  setCurrentActivity("reception");
                  setStep("auditing");
                }
              }}
              className={`p-4 rounded-xl flex items-center justify-between transition-all ${
                hsComplete
                  ? "bg-white shadow-sm border border-slate-200 cursor-pointer active:scale-95"
                  : "bg-slate-100 opacity-60 cursor-not-allowed border border-slate-200"
              }`}
            >
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-slate-100 text-slate-600 mr-3">
                  <ClipboardList className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-slate-800">
                    Reception
                  </h3>
                  <p className="text-sm text-slate-500">3 Elements</p>
                </div>
              </div>
              {!hsComplete && (
                <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded">
                  Locked
                </span>
              )}
              {hsComplete && <ChevronRight className="text-slate-400" />}
            </div>

            <div className="p-4 rounded-xl flex items-center justify-between bg-slate-100 opacity-60 cursor-not-allowed border border-slate-200">
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-slate-100 text-slate-600 mr-3">
                  <ClipboardList className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-slate-800">Kitchen</h3>
                  <p className="text-sm text-slate-500">8 Elements</p>
                </div>
              </div>
              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded">
                Locked
              </span>
            </div>

            {hsComplete && (
              <div className="pt-6">
                <button
                  onClick={() => setStep("review")}
                  className="w-full bg-slate-900 text-white font-bold py-4 rounded-xl shadow-md hover:bg-slate-800 transition-colors flex justify-center items-center"
                >
                  <PieChart className="w-5 h-5 mr-2" />
                  Review & Submit Audit
                </button>
              </div>
            )}
          </div>
        )}

        {/* STEP 3: Auditing Interface */}
        {step === "auditing" && (
          <div className="pb-24">
            <div className="bg-yellow-50 border-b border-yellow-200 p-3 text-sm text-yellow-800 flex items-center">
              <AlertTriangle className="w-4 h-4 mr-2 shrink-0" />
              Auto-saving enabled.
            </div>

            {(currentActivity === "hs"
              ? auditTemplate.healthAndSafety
              : auditTemplate.reception
            ).map((element, idx) => (
              <div
                key={element.id}
                className="bg-white border-b border-slate-200 p-5 flex flex-col"
              >
                <h3 className="text-lg font-bold text-slate-800 mb-4">
                  {idx + 1}. {element.name}
                </h3>

                {/* Scoring Logic */}
                {element.type === "passfail" && (
                  <div className="flex space-x-3 w-full">
                    <button
                      onClick={() =>
                        handleScore(element.id, "Pass", element.type)
                      }
                      className={`flex-1 py-4 rounded-xl font-bold text-lg flex items-center justify-center transition-all ${
                        scores[element.id] === "Pass"
                          ? "bg-green-500 text-white shadow-inner scale-95 border-2 border-green-600"
                          : "bg-slate-100 text-slate-600 border-2 border-transparent active:scale-95"
                      }`}
                    >
                      <Check className="w-6 h-6 mr-2" /> Pass
                    </button>
                    <button
                      onClick={() =>
                        handleScore(element.id, "Fail", element.type)
                      }
                      className={`flex-1 py-4 rounded-xl font-bold text-lg flex items-center justify-center transition-all ${
                        scores[element.id] === "Fail"
                          ? "bg-red-500 text-white shadow-inner scale-95 border-2 border-red-600"
                          : "bg-slate-100 text-slate-600 border-2 border-transparent active:scale-95"
                      }`}
                    >
                      <X className="w-6 h-6 mr-2" /> Fail
                    </button>
                  </div>
                )}

                {(element.type === "1-3" || element.type === "1-5") && (
                  <div className="flex space-x-2 w-full">
                    {[...Array(element.type === "1-3" ? 3 : 5)].map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() =>
                          handleScore(element.id, i + 1, element.type)
                        }
                        className={`flex-1 py-3 rounded-xl font-bold text-xl transition-all border-2 ${
                          scores[element.id] === i + 1
                            ? i + 1 === 1
                              ? "bg-red-500 text-white border-red-600"
                              : "bg-blue-500 text-white border-blue-600"
                            : "bg-slate-100 text-slate-600 border-transparent active:scale-95"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                )}

                {/* CRITICAL FAILURE WORKFLOW INLINE */}
                {criticalFails[element.id] && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4 animate-fade-in-down">
                    <div className="flex items-center text-red-700 font-bold mb-3">
                      <AlertTriangle className="w-5 h-5 mr-2" />
                      Critical Failure Protocol
                    </div>

                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                      Required Comment
                    </label>
                    <div className="relative mb-3">
                      <textarea
                        className="w-full p-3 pr-12 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none text-sm"
                        rows="2"
                        placeholder="Dictate or type issue details..."
                        value={criticalFails[element.id].comment}
                        onChange={(e) =>
                          updateCriticalFail(
                            element.id,
                            "comment",
                            e.target.value
                          )
                        }
                      ></textarea>
                      <button
                        onClick={() => setIsDictating(!isDictating)}
                        className={`absolute right-2 top-2 p-2 rounded-full transition-colors ${
                          isDictating
                            ? "bg-red-500 text-white animate-pulse"
                            : "text-slate-400 hover:bg-slate-100"
                        }`}
                        title="Voice to text dictation"
                      >
                        <Mic className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="flex justify-between items-center mb-1">
                      <label className="block text-sm font-semibold text-slate-700">
                        Evidence (Min 1, Max 3)
                      </label>
                      <span className="text-xs font-bold text-slate-500">
                        {criticalFails[element.id].photos?.length || 0}/3 Photos
                      </span>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        onClick={() => addPhoto(element.id)}
                        disabled={
                          (criticalFails[element.id].photos?.length || 0) >= 3
                        }
                        className={`flex-1 py-3 rounded-lg flex items-center justify-center font-semibold border-2 transition-colors ${
                          (criticalFails[element.id].photos?.length || 0) > 0
                            ? "bg-green-100 border-green-500 text-green-700"
                            : "bg-white border-red-300 text-red-600 hover:bg-red-100"
                        } ${
                          (criticalFails[element.id].photos?.length || 0) >= 3
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        {(criticalFails[element.id].photos?.length || 0) > 0 ? (
                          <>
                            <CheckCircle className="w-5 h-5 mr-2" /> Add Another
                          </>
                        ) : (
                          <>
                            <Camera className="w-5 h-5 mr-2" /> Take Photo
                          </>
                        )}
                      </button>
                    </div>

                    {/* Render photo thumbnails */}
                    {criticalFails[element.id].photos?.length > 0 && (
                      <div className="flex space-x-2 mt-2">
                        {criticalFails[element.id].photos.map((p, i) => (
                          <div
                            key={i}
                            className="w-12 h-12 bg-slate-200 rounded border border-slate-300 flex items-center justify-center relative"
                          >
                            <Camera className="w-4 h-4 text-slate-400" />
                            <span className="absolute -top-1 -right-1 bg-green-500 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold shadow">
                              {i + 1}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {(!criticalFails[element.id].comment ||
                      (criticalFails[element.id].photos?.length || 0) ===
                        0) && (
                      <p className="text-xs text-red-500 mt-2 font-medium">
                        * Complete fields to continue audit.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}

            <div className="p-4 mt-4">
              <button
                onClick={() => setStep("activities")}
                disabled={currentActivity === "hs" && !hsComplete}
                className={`w-full py-4 rounded-xl font-bold text-lg shadow-md transition-colors ${
                  currentActivity === "hs" && !hsComplete
                    ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {currentActivity === "hs" && !hsComplete
                  ? "Complete H&S to Proceed"
                  : "Save & Return to Areas"}
              </button>
            </div>
          </div>
        )}

        {/* STEP 4: Review & Submit */}
        {step === "review" && (
          <div className="p-6 space-y-6 animate-fade-in-up">
            <div className="text-center">
              <div
                className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center border-4 mb-4 ${
                  calculateScore() >= 90
                    ? "bg-green-100 border-green-500 text-green-600"
                    : "bg-red-100 border-red-500 text-red-600"
                }`}
              >
                <span className="text-3xl font-black">{calculateScore()}%</span>
              </div>
              <h2 className="text-2xl font-bold text-slate-800">
                {calculateScore() >= 90 ? "Audit Passed" : "Audit Failed"}
              </h2>
              <p className="text-slate-500 mt-2 text-sm">
                Aggregate score must be 90% or higher to pass the site.
              </p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <h3 className="font-bold text-slate-700 mb-3 border-b pb-2">
                Audit Summary
              </h3>
              <div className="flex justify-between items-center mb-2 text-sm">
                <span className="text-slate-600">Total Elements Scored</span>
                <span className="font-bold">{Object.keys(scores).length}</span>
              </div>
              <div className="flex justify-between items-center mb-2 text-sm">
                <span className="text-slate-600">
                  Critical Failures Generated
                </span>
                <span className="font-bold text-red-600">
                  {Object.keys(criticalFails).length}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-600">Photos Captured</span>
                <span className="font-bold">
                  {Object.values(criticalFails).reduce(
                    (acc, curr) => acc + (curr.photos?.length || 0),
                    0
                  )}
                </span>
              </div>
            </div>

            <button
              onClick={() => {
                setStep("select-site");
                setScores({});
                setCriticalFails({});
                setHsComplete(false);
              }}
              className="w-full bg-blue-600 text-white font-bold py-4 rounded-xl shadow-md hover:bg-blue-700 transition-colors flex justify-center items-center"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Digitally Sign & Submit
            </button>
          </div>
        )}
      </div>

      {/* Bottom Nav */}
      <div className="h-16 bg-white border-t border-slate-200 flex justify-around items-center text-slate-400 shrink-0">
        <div className="flex flex-col items-center text-blue-600">
          <LayoutDashboard className="w-6 h-6" />
          <span className="text-[10px] mt-1 font-medium">Audits</span>
        </div>
        <div className="flex flex-col items-center">
          <AlertTriangle className="w-6 h-6" />
          <span className="text-[10px] mt-1 font-medium">Actions</span>
        </div>
        <div className="flex flex-col items-center">
          <Settings className="w-6 h-6" />
          <span className="text-[10px] mt-1 font-medium">Settings</span>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   MANAGEMENT PORTAL PROTOTYPE (WEB)
   ========================================================================== */
function ManagerPortal() {
  const [activeTab, setActiveTab] = useState("dashboard"); // dashboard, actions, reports
  const [drillDownLevel, setDrillDownLevel] = useState(0); // 0=Top, 1=Regions, 2=Sites

  return (
    <div className="w-full max-w-7xl h-full flex bg-white rounded-xl shadow-xl overflow-hidden border border-slate-200">
      {/* Sidebar */}
      <div className="w-64 bg-slate-900 text-slate-300 flex flex-col">
        <div className="p-6">
          <h2 className="text-white font-bold text-2xl tracking-tight">
            AuditCore
            <span className="text-blue-500 text-4xl leading-[0px]">.</span>
          </h2>
          <p className="text-xs text-slate-500 uppercase tracking-widest mt-1">
            Management
          </p>
        </div>

        <nav className="flex-1 px-4 space-y-2 mt-4">
          <button
            onClick={() => {
              setActiveTab("dashboard");
              setDrillDownLevel(0);
            }}
            className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors ${
              activeTab === "dashboard"
                ? "bg-blue-600 text-white shadow-md"
                : "hover:bg-slate-800 hover:text-white"
            }`}
          >
            <BarChart3 className="w-5 h-5 mr-3" /> Dashboard
          </button>
          <button
            onClick={() => setActiveTab("actions")}
            className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors ${
              activeTab === "actions"
                ? "bg-blue-600 text-white shadow-md"
                : "hover:bg-slate-800 hover:text-white"
            }`}
          >
            <AlertOctagon className="w-5 h-5 mr-3" /> Corrective Actions
            <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              12
            </span>
          </button>
          <button
            onClick={() => setActiveTab("reports")}
            className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors ${
              activeTab === "reports"
                ? "bg-blue-600 text-white shadow-md"
                : "hover:bg-slate-800 hover:text-white"
            }`}
          >
            <FileText className="w-5 h-5 mr-3" /> Report Wizard
          </button>
          <button className="w-full flex items-center px-4 py-3 rounded-lg hover:bg-slate-800 hover:text-white transition-colors">
            <Settings className="w-5 h-5 mr-3" /> System Settings
          </button>
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center mr-3">
              <User className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-white">Admin User</p>
              <p className="text-xs text-slate-500">System Administrator</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col bg-slate-50 overflow-y-auto">
        {/* Header */}
        <header className="bg-white px-8 py-5 border-b border-slate-200 flex justify-between items-center sticky top-0 z-10">
          <h1 className="text-2xl font-bold text-slate-800 capitalize">
            {activeTab === "dashboard"
              ? drillDownLevel > 0
                ? "Dashboard Drill-Down"
                : "Regional Overview"
              : activeTab.replace("-", " ")}
          </h1>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                placeholder="Global search..."
                className="pl-10 pr-4 py-2 border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64 bg-slate-50"
              />
            </div>
          </div>
        </header>

        {/* Dashboard View */}
        {activeTab === "dashboard" && (
          <div className="p-8">
            {/* KPI Widgets */}
            <div className="grid grid-cols-5 gap-4 mb-8">
              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-blue-500">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-1">
                  Avg Score
                </p>
                <div className="flex items-end">
                  <span className="text-3xl font-black text-slate-800">
                    91.4
                  </span>
                  <span className="text-sm font-bold text-slate-500 ml-1">
                    %
                  </span>
                </div>
              </div>
              <div
                className="bg-white p-5 rounded-xl shadow-md border border-red-200 border-t-4 border-t-red-500 cursor-pointer hover:shadow-lg transition-all transform hover:-translate-y-1 relative overflow-hidden"
                onClick={() => setDrillDownLevel(1)}
              >
                <div className="absolute top-0 right-0 p-2 opacity-10">
                  <AlertOctagon className="w-12 h-12 text-red-500" />
                </div>
                <p className="text-xs font-semibold text-red-500 uppercase mb-1 flex items-center">
                  Crit Fails <ChevronRight className="w-3 h-3 ml-1" />
                </p>
                <span className="text-3xl font-black text-slate-800">14</span>
              </div>
              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-yellow-500">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-1">
                  Open Actions
                </p>
                <span className="text-3xl font-black text-slate-800">12</span>
              </div>
              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-green-500">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-1">
                  Audits Today
                </p>
                <span className="text-3xl font-black text-slate-800">142</span>
              </div>

              {/* NEW AI WIDGET from PDF enhancements */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-5 rounded-xl shadow-sm border border-indigo-100 border-t-4 border-t-purple-500 relative overflow-hidden">
                <div className="absolute -right-2 -top-2 opacity-20">
                  <Sparkles className="w-16 h-16 text-purple-600" />
                </div>
                <p className="text-xs font-semibold text-purple-700 uppercase mb-1 flex items-center">
                  AI Risk Prediction
                </p>
                <div className="mt-2 text-sm text-slate-700 font-medium leading-tight">
                  <span className="text-red-600 font-bold">
                    Wellington Central
                  </span>{" "}
                  is 40% more likely to fail Kitchen Audit this week.
                </div>
              </div>
            </div>

            {/* Drill Down Logic Simulation */}
            {drillDownLevel === 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-bold text-slate-800">
                    Performance by Region
                  </h3>
                  <button className="text-blue-600 text-sm font-medium hover:underline">
                    View All
                  </button>
                </div>
                <div className="space-y-4">
                  {["North Island", "South Island", "Australia"].map(
                    (region, i) => (
                      <div
                        key={region}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                        onClick={() => setDrillDownLevel(1)}
                      >
                        <span className="font-semibold text-slate-700">
                          {region}
                        </span>
                        <div className="flex items-center space-x-6 w-1/2">
                          <div className="flex-1 bg-slate-200 h-2 rounded-full overflow-hidden">
                            <div
                              className="bg-blue-500 h-full"
                              style={{ width: `${95 - i * 8}%` }}
                            ></div>
                          </div>
                          <span className="font-bold text-slate-600 w-12 text-right">
                            {95 - i * 8}%
                          </span>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-bold ${
                              i === 0
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {i === 0 ? "0 Criticals" : `${i * 7} Criticals`}
                          </span>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {drillDownLevel === 1 && (
              <div className="animate-fade-in-up">
                <button
                  onClick={() => setDrillDownLevel(0)}
                  className="text-sm text-blue-600 font-medium mb-4 flex items-center hover:underline"
                >
                  <ChevronRight className="w-4 h-4 rotate-180" /> Back to
                  Regions
                </button>
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 border-l-4 border-l-red-500">
                  <h3 className="text-xl font-bold text-slate-800 mb-1">
                    South Island Critical Failures
                  </h3>
                  <p className="text-sm text-slate-500 mb-6">
                    Click an element to jump directly to the corrective action
                    workflow.
                  </p>

                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-200 text-sm text-slate-500 uppercase tracking-wider">
                        <th className="pb-3 font-semibold">Site / Contract</th>
                        <th className="pb-3 font-semibold">Activity</th>
                        <th className="pb-3 font-semibold">Failed Element</th>
                        <th className="pb-3 font-semibold">Auditor</th>
                        <th className="pb-3 text-right font-semibold">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      <tr
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => setActiveTab("actions")}
                      >
                        <td className="py-4 font-bold text-slate-800">
                          Christchurch Depot
                          <br />
                          <span className="text-xs text-slate-500 font-normal">
                            CNT-3092
                          </span>
                        </td>
                        <td className="py-4 text-slate-600">Health & Safety</td>
                        <td className="py-4 font-medium text-red-600 flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-2" /> Fire
                          Extinguisher Missing
                        </td>
                        <td className="py-4 text-slate-600">J. Smith</td>
                        <td className="py-4 text-right">
                          <button className="bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg font-semibold hover:bg-blue-100">
                            Resolve
                          </button>
                        </td>
                      </tr>
                      <tr
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => setActiveTab("actions")}
                      >
                        <td className="py-4 font-bold text-slate-800">
                          Dunedin Office
                          <br />
                          <span className="text-xs text-slate-500 font-normal">
                            CNT-8812
                          </span>
                        </td>
                        <td className="py-4 text-slate-600">Reception</td>
                        <td className="py-4 font-medium text-red-600 flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-2" /> Door lock
                          broken (Score 1)
                        </td>
                        <td className="py-4 text-slate-600">M. Kelly</td>
                        <td className="py-4 text-right">
                          <button className="bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg font-semibold hover:bg-blue-100">
                            Resolve
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Corrective Actions View */}
        {activeTab === "actions" && (
          <div className="p-8 h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-800">
                Corrective Action Workflow
              </h2>
              <div className="flex space-x-2">
                <button className="bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-lg text-sm font-medium flex items-center shadow-sm">
                  <Filter className="w-4 h-4 mr-2" /> Filter
                </button>
              </div>
            </div>

            {/* Kanban Board Simulation */}
            <div className="flex flex-1 space-x-6 overflow-x-auto pb-4">
              {/* OPEN COLUMN */}
              <div className="w-1/3 min-w-[300px] flex flex-col">
                <div className="flex items-center justify-between mb-3 px-1">
                  <h3 className="font-bold text-slate-600 uppercase text-sm tracking-wider">
                    Open
                  </h3>
                  <span className="bg-slate-200 text-slate-600 text-xs font-bold px-2 py-1 rounded-full">
                    1
                  </span>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 border-t-4 border-t-red-500">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-bold text-slate-500">
                      CA-1042
                    </span>
                    <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded">
                      High Priority
                    </span>
                  </div>
                  <h4 className="font-bold text-lg text-slate-800 mb-1">
                    Fire Extinguisher Missing
                  </h4>
                  <p className="text-sm text-slate-600 mb-4">
                    Auckland City HQ • Health & Safety
                  </p>

                  <div className="bg-slate-100 p-3 rounded-lg mb-4 text-sm text-slate-700 italic border-l-4 border-slate-300">
                    "Bracket is empty. Extinguisher seems to have been removed
                    during weekend renovations." - Jane Doe
                  </div>

                  {/* Simulated Photo */}
                  <div className="w-full h-24 bg-slate-200 rounded-lg mb-4 border border-slate-300 flex items-center justify-center relative overflow-hidden group cursor-pointer">
                    <Camera className="text-slate-400 w-8 h-8" />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all"></div>
                  </div>

                  <button className="w-full bg-blue-600 text-white font-bold py-2 rounded-lg hover:bg-blue-700 transition-colors">
                    Assign Action
                  </button>
                </div>
              </div>

              {/* ASSIGNED COLUMN */}
              <div className="w-1/3 min-w-[300px] flex flex-col">
                <div className="flex items-center justify-between mb-3 px-1">
                  <h3 className="font-bold text-slate-600 uppercase text-sm tracking-wider">
                    Assigned / In Progress
                  </h3>
                  <span className="bg-slate-200 text-slate-600 text-xs font-bold px-2 py-1 rounded-full">
                    1
                  </span>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 border-t-4 border-t-blue-500">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-bold text-slate-500">
                      CA-1041
                    </span>
                    <span className="bg-yellow-100 text-yellow-700 text-xs font-bold px-2 py-1 rounded">
                      Medium
                    </span>
                  </div>
                  <h4 className="font-bold text-lg text-slate-800 mb-1">
                    Kitchen Sink Leaking
                  </h4>
                  <p className="text-sm text-slate-600 mb-4">
                    Wellington Central • Kitchen
                  </p>

                  <div className="flex items-center bg-blue-50 p-2 rounded-lg mb-4 border border-blue-100">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xs mr-3">
                      PM
                    </div>
                    <div className="text-sm">
                      <p className="font-semibold text-slate-800">
                        Assigned to: Paul M. (Maint)
                      </p>
                      <p className="text-xs text-slate-500">Due: Tomorrow</p>
                    </div>
                  </div>

                  <button className="w-full bg-white border border-slate-300 text-slate-700 font-bold py-2 rounded-lg hover:bg-slate-50 transition-colors">
                    Verify & Close
                  </button>
                </div>
              </div>

              {/* CLOSED COLUMN */}
              <div className="w-1/3 min-w-[300px] flex flex-col">
                <div className="flex items-center justify-between mb-3 px-1">
                  <h3 className="font-bold text-slate-600 uppercase text-sm tracking-wider">
                    Closed (Recent)
                  </h3>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 border-t-4 border-t-green-500 opacity-75">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-bold text-slate-500">
                      CA-0998
                    </span>
                    <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded flex items-center">
                      <CheckCircle className="w-3 h-3 mr-1" /> Resolved
                    </span>
                  </div>
                  <h4 className="font-bold text-lg text-slate-500 line-through mb-1">
                    Exit Sign Broken
                  </h4>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reports View (Dummy) */}
        {activeTab === "reports" && (
          <div className="p-8 flex items-center justify-center h-full">
            <div className="bg-white p-10 rounded-2xl shadow-lg border border-slate-200 text-center max-w-md">
              <FileText className="w-16 h-16 text-blue-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                Report Wizard Prototype
              </h2>
              <p className="text-slate-600 mb-6">
                In the full build, this section will allow users to construct
                dynamic, cross-filtered reports (Region + Contract + Activity)
                and export them instantly to PDF for clients.
              </p>
              <button className="bg-slate-900 text-white font-bold py-3 px-6 rounded-lg w-full">
                Interactive Wizard Coming Soon
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
